import { useEffect, useRef, useState, type PointerEvent } from "react";
import { NinjaArt } from "../art/NinjaArt";
import type { ItemKind } from "../content/items";
import { MODE_INFO, type Mode } from "../content/modes";
import { ninjaOfRank, STARTER_NINJA, type Ninja } from "../content/ninjas";
import { POWER_NAMES, RANKS } from "../content/ranks";
import { VARIANTS, variantName } from "../content/variants";
import { cardStats, type CardStats } from "../game/cardStats";
import { availableModes } from "../game/modes";
import { fighterFor, isKnown, isRecruited, recordAgainst } from "../game/roster";
import { frameFor, highestRank, lookFor, setModes } from "../game/sets";
import { MAX_STRIPES, nextStripeMark } from "../game/stripes";
import { trialFor } from "../game/trials";
import type { SaveState } from "../save/schema";
import { ItemPicker } from "./ItemPicker";
import { Modal } from "./Modal";
import { SlotRow } from "./SlotRow";
import { Stripes } from "./Stripes";
import "./DetailCard.css";

interface Props {
  rankIndex: number | null;
  save: SaveState;
  /** The strand whose look opens first. */
  mode: Mode;
  onClose: () => void;
  onWear: (rankIndex: number, mode: Mode) => void;
  /** Fight with this ninja from now on (recruited ninjas only). */
  onFight: (ninjaId: string) => void;
  /** Start a battle against this ninja. */
  onChallenge: (ninjaId: string) => void;
  /** Fit an item to a slot of this ninja's kit (null empties it). */
  onFit: (ninja: Ninja, kind: ItemKind, index: number, id: string | null) => void;
  /** Open the Dojo Shop (from the picker, when there's nothing to fit). */
  onShop: () => void;
}

/** A swipe must travel this far (CSS px) sideways to change card. */
const SWIPE = 50;

export function formatUnlockDate(d: string): string {
  try {
    return new Date(d + "T12:00").toLocaleDateString(undefined, { day: "numeric", month: "short" });
  } catch {
    return d;
  }
}

/** How to unlock a look the child doesn't hold yet. */
export function unlockHint(save: SaveState, mode: Mode, rankIndex: number): string {
  const r = RANKS[rankIndex]!;
  const spec = trialFor(mode, rankIndex);
  const known = Object.values(save.rank).some((x) => x >= rankIndex);
  if (!known) return `Get ${spec?.passMark} right in this rank's trial to reveal this ninja.`;
  const name = variantName(mode, r.name);
  const label = MODE_INFO[mode].label;
  return save.rank[mode] === rankIndex - 1
    ? `Pass the ${label} trial to unlock ${name}.`
    : `Reach ${r.name} in ${label} to unlock ${name}.`;
}

/**
 * The collectible-card view: the card itself (art, move, powers) beside the
 * child's record on it, with every rank and every strand's look a tap away.
 */
export function DetailCard(props: Props) {
  const { rankIndex, mode } = props;
  if (rankIndex === null || !RANKS[rankIndex]) return null;
  // Keyed so each opening starts from the card and look it was opened on.
  return <CardView key={`${rankIndex}-${mode}`} {...props} rankIndex={rankIndex} />;
}

function CardView({
  rankIndex: opened,
  save,
  mode,
  onClose,
  onWear,
  onFight,
  onChallenge,
  onFit,
  onShop,
}: Props & { rankIndex: number }) {
  const closeBtn = useRef<HTMLButtonElement>(null);
  const [picking, setPicking] = useState<{ kind: ItemKind; index: number } | null>(null);
  const swipe = useRef<{ x: number; y: number } | null>(null);
  const modes = availableModes(save.settings.ops);
  const [rankIndex, setRank] = useState(opened);
  // Show a look the child holds for this rank (the one asked for if they hold
  // it), so a rank earned in Add never opens locked under the Take look. A
  // look tapped on its tab sticks while they browse, locked or not.
  const heldLook = (i: number, preferred: Mode): Mode =>
    save.rank[preferred] >= i || !modes.some((x) => save.rank[x] >= i)
      ? preferred
      : lookFor(save.rank, save.settings.look, i, modes);
  const [picked, setShown] = useState<Mode | null>(null);
  const shown = picked ?? heldLook(rankIndex, mode);
  useEffect(() => closeBtn.current?.focus(), []);

  const last = RANKS.length - 1;
  const stepRank = (d: number) => setRank((i) => Math.max(0, Math.min(last, i + d)));
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") setRank((i) => Math.max(0, i - 1));
      if (e.key === "ArrowRight") setRank((i) => Math.min(last, i + 1));
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [last]);

  const r = RANKS[rankIndex]!;
  const m = modes.includes(shown) ? shown : (modes[0] ?? "add");
  const here = save.rank[m];
  const locked = rankIndex > here;
  const known = (i: number) => isKnown(save.rank, save.roster, ninjaOfRank(i));
  const mystery = locked && !known(rankIndex);
  const ninja = ninjaOfRank(rankIndex);
  const recruited = isRecruited(save.roster, ninja.id);
  const fighting = fighterFor(save.roster, save.settings.fighter).id === ninja.id;
  const frame = frameFor(save.sets, rankIndex);
  const name = mystery ? "Mystery ninja" : variantName(m, r.name);
  const isCurrent = rankIndex === here;
  const stats = cardStats(save, m, rankIndex);
  const wearing =
    lookFor(save.rank, save.settings.look, rankIndex, setModes(save.settings.ops)) === m;

  const onPointerDown = (e: PointerEvent) => {
    swipe.current = { x: e.clientX, y: e.clientY };
  };
  const onPointerUp = (e: PointerEvent) => {
    const start = swipe.current;
    swipe.current = null;
    if (!start) return;
    const dx = e.clientX - start.x;
    if (Math.abs(dx) >= SWIPE && Math.abs(dx) > Math.abs(e.clientY - start.y)) {
      stepRank(dx < 0 ? 1 : -1);
    }
  };

  return (
    <Modal open label="Ninja card" onClose={onClose}>
      <div className="dview">
        <div className="dcard-col">
          <div className="dcard-row">
            <button
              type="button"
              className="rank-step"
              aria-label="Previous ninja"
              disabled={rankIndex === 0}
              onClick={() => stepRank(-1)}
            >
              ‹
            </button>
            <div
              className={"tcard pop" + (frame ? ` frame-${frame}` : "")}
              style={{ "--c": r.colour } as React.CSSProperties}
              key={rankIndex}
              onPointerDown={onPointerDown}
              onPointerUp={onPointerUp}
              onPointerCancel={() => (swipe.current = null)}
            >
              <div className="tc-top">
                <h3 className="dname display">{name}</h3>
                <span className="tc-rank display" role="img" aria-label={`Rank ${rankIndex + 1}`}>
                  {rankIndex + 1}
                </span>
              </div>
              <div className="tc-window">
                <NinjaArt
                  rank={r}
                  variant={m}
                  locked={locked}
                  stripes={isCurrent ? stats.stripes : 0}
                  className="tc-art"
                />
              </div>
              <div className="tc-type">
                <span>
                  {VARIANTS[m].name} look · {MODE_INFO[m].label}
                </span>
                {frame && (
                  <span className={"crest" + (frame === "foil" ? " foil" : "")}>
                    {frame === "foil" ? "Foil card" : "Gold card"}
                  </span>
                )}
              </div>
              <div className="move">
                <span className="move-label">Special move</span>
                <span className="display">{mystery ? "???" : r.move}</span>
              </div>
              <div className="powers">
                {POWER_NAMES.map((n, k) => (
                  <div className="pw" key={n}>
                    <span>{n}</span>
                    <span className="bar" aria-hidden="true">
                      <i style={{ width: `${mystery ? 0 : (r.powers[k] ?? 0) * 10}%` }} />
                    </span>
                    <b>{mystery ? "?" : r.powers[k]}</b>
                  </div>
                ))}
              </div>
              <SlotRow
                ninja={ninja}
                loadouts={recruited ? save.settings.loadout : undefined}
                items={save.items}
                locked={!recruited}
                className="tc-slots"
              />
              <p className="tc-motto">{mystery ? "A ninja yet to be revealed." : r.motto}</p>
            </div>
            <button
              type="button"
              className="rank-step"
              aria-label="Next ninja"
              disabled={rankIndex === last}
              onClick={() => stepRank(1)}
            >
              ›
            </button>
          </div>
          <div className="rank-dots" role="group" aria-label="Ninjas">
            {RANKS.map((x, i) => (
              <button
                key={x.name}
                type="button"
                className={"rank-dot display" + (i > here ? " locked" : "")}
                style={{ "--dot": known(i) ? x.colour : "var(--ink-soft)" } as React.CSSProperties}
                aria-pressed={i === rankIndex}
                aria-label={`Rank ${i + 1}, ${known(i) ? variantName(m, x.name) : "Mystery ninja"}`}
                onClick={() => setRank(i)}
              >
                {i + 1}
              </button>
            ))}
          </div>
        </div>

        <div className="dside">
          <div className="dpick">
            {modes.length > 1 && (
              <div className="looks" role="group" aria-label="Looks">
                {modes.map((x) => {
                  const held = save.rank[x] >= rankIndex;
                  return (
                    <button
                      key={x}
                      type="button"
                      className={"look-tab" + (held ? " held" : "")}
                      style={{ "--dot": MODE_INFO[x].colour } as React.CSSProperties}
                      aria-pressed={x === m}
                      aria-label={`${MODE_INFO[x].label} look${held ? "" : ", locked"}`}
                      onClick={() => setShown(x)}
                    >
                      <NinjaArt rank={r} variant={x} locked={!held} className="look-art" />
                      <span className="look-name">{VARIANTS[x].name}</span>
                    </button>
                  );
                })}
              </div>
            )}
            {locked && <p className="dmotto">{unlockHint(save, m, rankIndex)}</p>}
            {!locked &&
              rankIndex === highestRank(save.rank, save.settings.ops) &&
              modes.length > 1 && (
                <button
                  type="button"
                  className="mini wear"
                  aria-pressed={wearing}
                  onClick={() => onWear(rankIndex, m)}
                >
                  {wearing ? "Wearing this look" : "Wear this look"}
                </button>
              )}
          </div>
          <Record
            stats={stats}
            rankIndex={rankIndex}
            isCurrent={isCurrent}
            isNext={rankIndex === here + 1}
          />
          <div className="droster">
            <div className="facts">
              <div>
                <b>
                  {ninja.id === STARTER_NINJA
                    ? "From the start"
                    : save.roster[ninja.id]?.recruited
                      ? formatUnlockDate(save.roster[ninja.id]!.recruited!)
                      : "Not yet"}
                </b>
                <span>Recruited</span>
              </div>
              <div>
                <b>
                  {recordAgainst(save.roster, ninja.id).won} of{" "}
                  {recordAgainst(save.roster, ninja.id).fought}
                </b>
                <span>Battles won</span>
              </div>
            </div>
            <p className="dmotto">
              {recruited
                ? `${ninja.move.name}: ${ninja.move.text.toLowerCase()}.`
                : `Beat ${mystery ? "this ninja" : ninja.name} in battle to recruit ${mystery ? "it" : "them"}. ${ninja.health} health.`}
            </p>
            {recruited && (
              <div className="dkit">
                <SlotRow
                  ninja={ninja}
                  loadouts={save.settings.loadout}
                  items={save.items}
                  onPick={(kind, index) => setPicking({ kind, index })}
                  className="dkit-slots"
                />
                <span className="dkit-text">Tap a slot to fit an item.</span>
              </div>
            )}
            <div className="droster-row">
              {recruited && (
                <button
                  type="button"
                  className="mini wear"
                  aria-pressed={fighting}
                  onClick={() => onFight(ninja.id)}
                >
                  {fighting ? "Your fighter" : "Fight with this ninja"}
                </button>
              )}
              <button type="button" className="mini" onClick={() => onChallenge(ninja.id)}>
                {recruited ? "Rematch" : "Challenge"}
              </button>
            </div>
          </div>
          <button ref={closeBtn} type="button" className="big display dclose" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
      <ItemPicker
        slot={picking ? { ninja, ...picking } : null}
        loadouts={save.settings.loadout}
        items={save.items}
        onChoose={(id) => {
          if (picking) onFit(ninja, picking.kind, picking.index, id);
          setPicking(null);
        }}
        onClose={() => setPicking(null)}
        onShop={onShop}
      />
    </Modal>
  );
}

/** The child's record on one card: trial facts, stripes and recent tries. */
function Record({
  stats,
  rankIndex,
  isCurrent,
  isNext,
}: {
  stats: CardStats;
  rankIndex: number;
  isCurrent: boolean;
  isNext: boolean;
}) {
  const { spec, held, best, tries, passedOn, toGo, stripes, recent } = stats;
  const status = !held
    ? spec && best > 0
      ? `${toGo} more`
      : "Not yet"
    : rankIndex === 0
      ? "From the start"
      : passedOn
        ? formatUnlockDate(passedOn)
        : "Unlocked";
  const nextMark = spec && isNext ? nextStripeMark(best, spec.stripes) : null;
  const showStripes = (isCurrent && rankIndex < RANKS.length - 1) || (isNext && spec !== null);
  const top = Math.max(spec?.passMark ?? 0, ...recent.map((t) => t.score), 1);
  return (
    <div className="dstats">
      <div className="facts">
        <div>
          <b>{spec ? spec.passMark : "–"}</b>
          <span>Pass mark</span>
        </div>
        <div>
          <b>{best}</b>
          <span>Your best</span>
        </div>
        <div>
          <b>{status}</b>
          <span>{!held ? "To pass" : passedOn ? "Passed" : "Card"}</span>
        </div>
        <div>
          <b>{tries}</b>
          <span>Trials tried</span>
        </div>
      </div>
      {showStripes && (
        <p className="dstripes">
          <Stripes count={stripes} />{" "}
          {isCurrent
            ? `${stripes} of ${MAX_STRIPES} stripes towards the next rank.`
            : `${stripes} of ${MAX_STRIPES} stripes on this trial.` +
              (nextMark ? ` Next stripe at ${nextMark}.` : "")}
        </p>
      )}
      {recent.length > 0 && spec && (
        <figure className="tries">
          <figcaption>Your last tries</figcaption>
          <div
            className="tries-chart"
            role="img"
            aria-label={`Your last ${recent.length} tries: ${recent.map((t) => t.score).join(", ")}. Pass mark ${spec.passMark}.`}
          >
            <span
              className="tries-pass"
              style={{ bottom: `${(spec.passMark / top) * 100}%` }}
              aria-hidden="true"
            />
            {recent.map((t, i) => (
              <i
                key={i}
                className={t.passed ? "passed" : undefined}
                style={{ height: `${Math.max(4, (t.score / top) * 100)}%` }}
              />
            ))}
          </div>
        </figure>
      )}
    </div>
  );
}
